import {
  Box,
  Button,
  InsuranceIcon,
  LoadingIndicator,
  NavList,
} from '@deckee/deck-hand';
import React, { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { getAllSignalsSharedWithCurrentMember } from '../../../../services/store/modules/signal';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import MemberAvatar from '../../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import { ReactSvg, deckeeLogo } from '../../../shared/vector-images';
import useMemberGroups from '../hooks/use-member-groups';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const GroupsListPage: FC = () => {
  const { groups, error, isLoading } = useMemberGroups();

  const safetyContacts = useSelector(getAllSignalsSharedWithCurrentMember);
  const history = useHistory();
  const ref = useRef<HTMLDivElement>(null);

  const safetyContactsCount = safetyContacts.length;

  const handleGroupClick = (groupId: string, groupName: string) => {
    captureEvent({
      type: 'signal: navigate to group',
      payload: {
        groupId,
        groupName,
      },
      sendToAmplitude: true,
    });
    history.push(`/maps/signal/groups/${groupId}`);
  };

  const handleCreateNewGroup = () => {
    captureEvent({
      type: 'signal: create new group clicked',
      payload: {},
      sendToAmplitude: true,
    });

    openWithInAppBrowser({ url: 'https://deckee.com/create-group' });
  };

  return (
    <SheetLayout
      showMenuBar={false}
      showMapOverlay
      heading="Your groups"
      slideUpTransition
    >
      <SheetBody maxHeight="50vh" overflowY="auto" ref={ref}>
        {isLoading && <LoadingIndicator />}
        {error && (
          <Box p={3} textAlign="center" color="darkergrey">
            Error loading groups
          </Box>
        )}
        {!isLoading && !error && (
          <>
            <NavList>
              <NavList.Item
                chevron
                p="3"
                onClick={() =>
                  handleGroupClick('community', 'DECKEE Community')
                }
              >
                <IconWithTextLockup
                  image={
                    <IconWrapper>
                      <ReactSvg
                        toDisplay={deckeeLogo()}
                        options={{ fillColor: 'white' }}
                        width={28}
                        height={28}
                      />
                    </IconWrapper>
                  }
                  heading="DECKEE Community"
                  description="10K+ members"
                />
              </NavList.Item>
              <NavList.Item
                chevron
                p="3"
                onClick={() =>
                  handleGroupClick('safety-contacts', 'Safety Contacts')
                }
              >
                <IconWithTextLockup
                  image={
                    <IconWrapper>
                      <InsuranceIcon size={28} />
                    </IconWrapper>
                  }
                  heading="People you are a safety contact of"
                  description={`${safetyContactsCount} member${
                    safetyContactsCount !== 1 ? 's' : ''
                  }`}
                />
              </NavList.Item>
              {groups.map((group) => (
                <NavList.Item
                  key={group.id}
                  chevron
                  p="3"
                  onClick={() => handleGroupClick(group.id, group.name)}
                >
                  <IconWithTextLockup
                    image={
                      <MemberAvatar
                        memberAvatarUrl={group.iconUrl}
                        width={50}
                        height={50}
                      />
                    }
                    heading={group.name}
                    description={`${group.members.length} member${
                      group.members.length !== 1 ? 's' : ''
                    }`}
                  />
                </NavList.Item>
              ))}
            </NavList>
            <Box p={3}>
              <Button wide variant="dashed" onClick={handleCreateNewGroup}>
                Create new group
              </Button>
            </Box>
          </>
        )}
      </SheetBody>
    </SheetLayout>
  );
};

export default GroupsListPage;
